import { STORY_SOURCE, story } from '@/content/storyBuilder'

// Borusan and Lila Kağıt still have no PDF, but they do have a film — so the
// two lists are not the same list, and neither implies the other.
//
// The twelfth written page. Everything below comes from the four-part
// full-page capture of digitopia.co/customers/borusan-otomotiv-grup/ sent on
// 2026-08-28 — "tastamam şekilde, aynısını, tıpkısını tasarla" — with the one
// photograph on it, the show car, which the reference runs full width between
// the opening quote and the story. The film already sat in the header, which
// is where the reference puts it, so nothing above the fold changed but the
// lede.
//
// Its shape is the reference's and not the template's usual, in one way that
// is worth naming: **there is no `intro`**. The reference opens not with a
// grey standfirst band saying who the company is but with Aslı de Munnink's
// own words, centred on the white page — so that is a headless `centred`
// section carrying the wide photograph under it, which is the one arrangement
// that puts the quote above the picture. Everything after it is left-ranged
// prose on white, with no grey panels and no lists anywhere.
//
// The closing quote is not a `section`: her words have been in
// `content/testimonials.ts` keyed to this slug since the Hall of Fame
// shipped, Borusan Otomotiv's mark and her portrait included, and `StoryBody`
// draws that card — the testimonial template the same request asked for at
// the foot of the page. The 600×600 portrait sent with the capture is not
// used: the file already in `photo-src/` is the same photograph at 900×900
// and cut out against transparency, which is what that card needs.
export const added = story('borusan-otomotiv-grup', 'Borusan Otomotiv Grup', {
  video: 'F5sGZvrf6u8',
  lede: ['All systems go…'],
  sections: [
    {
      centred: true,
      body: [
        '“Our main goal was not to only get a score. We wanted the high-level management and '
        + 'business units to be on the same page during our digital transformation journey. '
        + 'Digitopia has made a great contribution to the unification of these two sides. In '
        + 'all six sessions, we had various discussions looking into the next two years from '
        + 'today.”',
      ],
      photo: {
        src: '/photo/borusan-mini-show-car-stage.webp',
        alt: 'A silver electric MINI with a green roof on a lit studio set of coral, blue and '
          + 'purple panels, a model in a sequinned cape walking past it.',
      },
    },
    {
      heading: 'Who is Borusan Otomotiv Group?',
      body: [
        'Borusan Otomotiv Group includes six companies and serves in the premium automobile '
        + 'segment.',
        'Borusan Otomotiv Group, a partnership of Borusan Holding and Giwa Holding operating '
        + 'in Germany, has been the distributor of BMW since 1984 and in 1992, BMW Motorrad, '
        + 'the favorite of motorcycle enthusiasts, in 1998, Land Rover, the manufacturer of '
        + 'the best 4×4 vehicles, in 1999. The brand range was completed with the '
        + 'participation of MINI, which also made a difference in the small car segment, and '
        + 'lastly, Jaguar, which represents the combination of luxury, comfort and performance '
        + 'in 2014.',
        'Borusan Otomotiv Group has been serving as the distributor of BMW, MINI and BMW '
        + 'Motorrad, Jaguar and Land Rover brands for more than 35 years.',
      ],
    },
    {
      heading: 'Project Background',
      body: [
        'In Aslı de Munnink’s own words…',
        'At Borusan Otomotiv Group, digital transformation started in 2017. I joined Borusan '
        + 'in 2018, and I am the first CDO of the company and in the group companies as a '
        + 'whole. As you can imagine, digital transformation necessitates a great amount of '
        + 'effort for a group firm of six companies. At the beginning of our initiatives, '
        + 'Information Technologies were the main priority in digital transformation. However, '
        + 'in 2018 we shifted it to another level. To differentiate ourself in the market, we '
        + 'focused on digital transformation in customer experience.',
        'I wanted to get our digital maturity score measured when I first joined the company. '
        + 'However, we preferred to focus on other priorities at that time. One of the main '
        + 'reasons was some of the projects had already started and we needed to see some '
        + 'solid results. Also, we have high level goals in the digital team, that’s why we '
        + 'didn’t want to create digital fatigue between the business units and the board.',
        'When 2019 arrived, our priorities had changed and we focused on investing in '
        + 'technology. To strengthen the technological infrastructure we completed major '
        + 'changes and considered our customer experience goals. During that time, we built '
        + 'our digital strategy and data strategies.',
        '2020 was the game changer for everyone. Digitalization became faster than ever, and '
        + 'our company was no exception. Our primary focus was how to use artificial '
        + 'intelligence and machine learning in data analytics. With the global pandemic, we '
        + 'have also taken a step towards real-time data management. And finally, end of 2020 '
        + 'is the right time to know our Digital Maturity Score.',
      ],
    },
    {
      heading: 'Why Digitopia, Why DMI?',
      body: [
        'We made a very careful selection. We chose Digitopia among many alternative '
        + 'companies. First, we needed to see where we were in the local market at the first '
        + 'place, then in the globe. Second, the consulting company must guarantee an '
        + 'independent, holistic, and end-to-end service. We required an evaluation within '
        + 'these 6 dimensions; Governance, Customer, People, Innovation, Marketing, and '
        + 'Technology, which Digitopia includes in their methodology. Digitopia was the only '
        + 'one to answer all of these prerequisites.',
      ],
    },
    {
      heading: 'The Process and The Outcome',
      body: [
        'We did not plan for the consultancy process to be conducted online, but the pandemic '
        + 'pushed us to this and Digitopia managed this process so well that all participants '
        + 'expressed their opinions with candor. This had a very positive effect on the '
        + 'results.',
        'On the other hand, our main goal was not to only get a score. We wanted the '
        + 'high-level management and business units to be on the same page during our digital '
        + 'transformation journey. Digitopia has made a great contribution to the unification '
        + 'of these two sides. In all six sessions, we had various discussions looking into '
        + 'next two years from today.',
        'Borusan Otomotiv Group’s Digital Maturity score was above the market average. Seeing '
        + 'that our work and efforts had yielded such results motivated us even more. The '
        + 'initiatives report prepared by Digitopia and the action plan that we prepared '
        + 'overlapped 90%. We have proven that we are on the right track. What we will do from '
        + 'now on is to protect our strengths and focus on the points where we need to improve.',
        'When we learned our maturity score at the end of the 6 sessions, Digitopia '
        + 'highlighted that our aim is not to see the score but to take the necessary actions '
        + 'to reach our goals. When we look at the big picture, a win-win situation occurred '
        + 'at both the board and business unit levels.',
        'Throughout the process, the Digitopia team showed that they know our projects as well '
        + 'as we do. They acted like part of our team and during the sessions, they allowed '
        + 'our teammates to contribute to the process without any prejudice or biases.',
        'We see the positive effects of the interim quarterly evaluations to ensure the '
        + 'continuity of the DMI process. In our opinion, Digital Maturity is not a job that '
        + 'should be evaluated once a year and then put on the shelves to collect dust. '
        + 'Evaluating our actions every three months and six months allows us to see our '
        + 'progress and motivates us even more for our long-term goals. In this way, we can be '
        + 'sure that our goals in the next two years will be actualised. We look forward to '
        + 'using Digitopia DMI as a tool within our Digital Team in the coming years.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a four-part full-page capture of '
    + `${STORY_SOURCE}/borusan-otomotiv-grup/ supplied on 2026-08-28, not from a file we hold, `
    + 'so please proofread it against the source before production — particularly the dates and '
    + 'brands in “Who is Borusan Otomotiv Group?” (BMW 1984, BMW Motorrad 1992, Land Rover '
    + '1998, MINI 1999, Jaguar 2014, “more than 35 years”), which the reference sets as one '
    + 'long sentence whose years and marques do not obviously line up, and the six dimensions '
    + 'named under “Why Digitopia, Why DMI?” — Governance, Customer, People, Innovation, '
    + 'Marketing and Technology — which are that page’s own and differ from the six the Petrol '
    + 'Ofisi story names. Six plain typographic slips on the reference were corrected rather '
    + 'than copied, none of them a claim: “Borusan Otomotive Group”, “differantiate”, '
    + '“artifical intellegince”, “guarentee”, and the spacing and apostrophes in “that ’s why '
    + 'we didnt”. One grammatical oddity was left verbatim because it is inside a customer’s '
    + 'own words: “to differentiate ourself in the market”. '
    + '**One thing to decide: the spelling of her name.** The reference page writes “Aslı de '
    + 'Munnink” in its own prose and “ASLI DE MUNNINK” under the quote, and the portrait sent '
    + 'with the capture is filenamed the same way, so this page uses Munnink — and '
    + 'content/testimonials.ts, which had read “Asli de Munnik” since the Hall of Fame shipped, '
    + 'was changed to match. That changes her card on /en/testimonials as well as the one at '
    + 'the foot of this page; it is two lines to put back if the shorter spelling is the right '
    + 'one. Three deliberate differences from the reference, each of them a template-wide '
    + 'decision rather than this page’s: the header eyebrow reads “Digitopia / Customer Success '
    + 'Story” where digitopia.co reads “SUCCESS STORIES”; the reference’s centred “Download” '
    + 'button below “The Process and The Outcome” is not drawn at all, because this story has '
    + 'no PDF — it was not among the sixteen documents sent on 2026-08-27, and inventing a '
    + 'destination for a button is the mistake `importedHref` exists to avoid; and the quote '
    + 'card at the foot is the Hall of Fame template as asked for, so it carries the company as '
    + 'its eyebrow and both of her paragraphs, where the reference shows the second one alone. '
    + 'One production note: the photograph is a 1200×455 original, which is what the live site '
    + 'serves, so it is drawn at that width and will look soft on a high-density screen until a '
    + 'larger file arrives.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 16
